import { Tag } from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import { BookingGradeRemaining } from "~/components/BookingGradeRemaining"
import { TradeKinds } from "~/constants/Kinds"

const groupItems = (items: any[]) =>
  Object.values(
    items.reduce((acc: any, item: any) => {
      const key =
        item?.commodity_variety?.id ??
        item?.commodity?.id ??
        (item?.commodity_variety?.name || item?.commodity?.name || "na")
      if (!acc[key]) {
        acc[key] = {
          commodity: item?.commodity,
          variety: item?.commodity_variety,
          grades: [],
        }
      }
      acc[key].grades.push(item)
      return acc
    }, {})
  )

export const MULTI_COMMODITY_BUYER_COLUMN = {
  Header: "Buyer",
  id: "item_buyer",
  Cell({ row }: any) {
    const original = row?.original
    if (!original?._buyerFirst) return null
    return (
      <Cell
        title={original?._buyer?.user?.[0]?.full_name || original?._buyer?.name}
      />
    )
  },
}

export const MULTI_COMMODITY_ITEM_COLUMNS = [
  {
    Header: "Commodity",
    id: "item_commodity",
    Cell({ row }: any) {
      const group = row?.original?._group
      if (!group || !row?.original?._groupFirst) return null
      return (
        <Cell
          title={group.variety?.name || group.commodity?.name || "--"}
          subtitle={
            group.variety?.name && group.commodity?.name
              ? group.commodity?.name
              : undefined
          }
        />
      )
    },
  },
  {
    Header: "Grade",
    accessor: "variety_grade.name",
    Cell({ value }: any) {
      return (
        <Cell
          title={
            <Tag intent="info" variant="minimal" className="!rounded-xl">
              {value}
            </Tag>
          }
        />
      )
    },
  },
  {
    Header: "Quantity",
    accessor: "quantity",
    Cell({ value, row: { original } }: any) {
      if (!value && value !== 0) return "--"
      return (
        <Cell
          title={String(value) + " Units"}
          titleClassName="!text-green font-600"
        />
      )
    },
  },
  {
    Header: "Remaining Quantity",
    id: "item_remaining_quantity",
    Cell({ row: { original } }: any) {
      if (
        original?._trade_kind === TradeKinds.HishobPatti ||
        !original?._remaining_quantity
      )
        return "--"
      return (
        <Cell
          title={
            <BookingGradeRemaining
              tradeId={original?._trade_id}
              gradeId={original?.variety_grade?.id}
            />
          }
          titleClassName="!text-green font-600"
        />
      )
    },
  },
  {
    Header: "Rate",
    accessor: "rate",
    Cell({ value, row: { original } }: any) {
      if (!value && value !== 0) return "--"
      return (
        <Cell
          title={`${inr(value)} / ${original?.rate_unit?.name || "na"}`}
          titleClassName="!text-brown font-600"
        />
      )
    },
  },
]

export const MultiCommoditySubRow = ({ row, colSpan, showBuyer }: any) => {
  const original = row?.original
  if (!original?.is_multiple_commodity) return null

  let data: any[] = []

  if (original?.trades?.length) {
    for (const trade of original.trades) {
      const tradeItems = (trade?.trade_items || []).filter(
        (item: any) => item?.id
      )
      if (!tradeItems.length) continue
      const groups = groupItems(tradeItems)
      const buyerRows = groups.flatMap((group: any) =>
        group.grades.map((item: any, gi: number) => ({
          ...item,
          _buyer: trade?.buyer,
          _remaining_quantity: trade?.deducted_quantity_remaining,
          _trade_id: trade?.id,
          _group: group,
          _groupFirst: gi === 0,
          _trade_kind: original?.kind,
        }))
      )
      buyerRows.forEach((r, i) => {
        r._buyerFirst = i === 0
        r._buyerSpan = buyerRows.length
      })
      data = data.concat(buyerRows)
    }
  } else {
    const items = (original?.trade_items || []).map((item: any) => ({
      ...item,
      _remaining_quantity: original?.deducted_quantity_remaining,
      _trade_id: original?.id,
    }))
    const filtered = items.filter((item: any) => item?.id)
    if (!filtered.length) return null
    const groups = groupItems(filtered)
    data = groups.flatMap((group: any) =>
      group.grades.map((item: any, gi: number) => ({
        ...item,
        _group: group,
        _groupFirst: gi === 0,
        _trade_kind: original?.kind,
      }))
    )
  }

  if (!data.length) return null

  const columns = showBuyer
    ? [MULTI_COMMODITY_BUYER_COLUMN, ...MULTI_COMMODITY_ITEM_COLUMNS]
    : MULTI_COMMODITY_ITEM_COLUMNS

  return (
    <td colSpan={colSpan}>
      <div className="px-3 py-2 bg-gray-50">
        <TableView
          relative={false}
          columns={columns}
          data={data}
          className="!overflow-visible !shadow-none !m-0"
          tableClassName="!shadow-none !m-0 !overflow-visible"
          getRowProps={() => ({
            className: "align-top",
          })}
          getHeaderProps={() => ({
            className: "border-b-1 border-gray-300 bg-gray-100",
          })}
          getCellProps={(cell: any) => {
            if (showBuyer && cell.column.id === "item_buyer") {
              if (cell.row.original?._buyerFirst)
                return { rowSpan: cell.row.original._buyerSpan }
              return { className: "hidden" }
            }
            if (cell.column.id !== "item_commodity") return undefined
            if (cell.row.original?._groupFirst)
              return { rowSpan: cell.row.original._group.grades.length }
            return { className: "hidden" }
          }}
        />
      </div>
    </td>
  )
}

export const renderMultiCommoditySubRow =
  (colSpan: number, options?: { showBuyer?: boolean }) =>
  ({ row }: any) =>
    (
      <MultiCommoditySubRow
        row={row}
        colSpan={colSpan}
        showBuyer={options?.showBuyer}
      />
    )
